import React from 'react';
import { Trans } from '@lingui/macro';

import { Box, Flex, Link } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import HowRoverWorks, { ImageOptions } from '@rover/react-lib/src/components/HowRoverWorks';
import LandingHero from '@rover/react-lib/src/pages/landing/components/LandingHero';
import Guarantees from '@rover/react-lib/src/pages/landing/ServiceLanding/components/Guarantees';
import ServiceLandingServices from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServices';
import ServiceTargetAudience from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceTargetAudience';
import ServiceValueProposition from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceValueProposition';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import DogBoardingServiceFaqs from '../../components/ServiceFaqs/DogBoardingServiceFaqs';
import ServiceLandingResultsLayout from '../../components/ServiceLandingResultsLayout';

const DogBoarding: React.FC<Props> = ({
  serviceLinks,
  countryConfig,
  citiesAndNeighborhoods,
  language,
  serviceLandingData,
  serviceLinksWithDisplayName,
  searchResults,
  frontendConfigSettings,
  adwordslocation,
  serviceTypes,
}) => {
  const serviceLandingType = ServiceLandingEnum.DOG_BOARDING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero mb="20x">
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot languageCode={language} />
        </LandingHero.Content>
      </LandingHero>
      {searchResults && searchResults.results.length && (
        <ServiceLandingResultsLayout
          serviceLandingType={serviceLandingType}
          language={language}
          countryConfig={countryConfig}
          searchResults={searchResults}
          mapSettings={frontendConfigSettings}
          adwordslocation={adwordslocation}
        />
      )}
      <Section mt="0x" mb="40x">
        <Flex alignItems="center" flexDirection="column">
          <ServiceValueProposition>
            <ServiceValueProposition.Display>
              <Trans>No more loud kennels or cages</Trans>
            </ServiceValueProposition.Display>
            <ServiceValueProposition.List>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Loving care in your sitter's home</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    It's easy to find dog boarding in your city—just search your zip code to see
                    trusted sitters near you.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Dog sitters you can trust</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Every sitter profile has been individually reviewed and approved by Rover and
                    sitters on Rover have received millions of 5-star reviews.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Affordable, personalized pet care</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Worried that dog boarding in a sitter’s home will be more expensive than
                    kennels? Find out{' '}
                    <Link href="/dog-sitting-rates/" target="_blank">
                      how much
                    </Link>{' '}
                    dog sitters near you typically cost.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Easy Meet & Greets</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Meet your dog's perfect match before you book. Request a free in-person Meet &
                    Greet, right from your Rover inbox.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Cute photo updates</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Millions of adorable photos have been sent through Rover! Receive a pic of your
                    dog enjoying a round of fetch.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
            </ServiceValueProposition.List>
          </ServiceValueProposition>

          <HowRoverWorks>
            <HowRoverWorks.Display>
              <Trans>How to book dog boarding</Trans>
            </HowRoverWorks.Display>
            <HowRoverWorks.List>
              <HowRoverWorks.ListItem image={ImageOptions.MAGNIFYING_GLASS}>
                <HowRoverWorks.ItemHeading step={1}>
                  <Trans>Search Pet Sitters</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    Browse profiles of sitters near you who offer overnight dog boarding.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
              <HowRoverWorks.ListItem image={ImageOptions.COLLAR}>
                <HowRoverWorks.ItemHeading step={2}>
                  <Trans>Meet In Person</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>Schedule a free in-person Meet & Greet to find the perfect fit.</Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
              <HowRoverWorks.ListItem image={ImageOptions.PHONE}>
                <HowRoverWorks.ItemHeading step={3}>
                  <Trans>Book and Pay</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    It’s that easy! All payments are processed through Rover's secured platform.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
            </HowRoverWorks.List>
          </HowRoverWorks>

          <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />

          <ServiceTargetAudience data-qa-id="service-landing-description">
            <ServiceTargetAudience.Display>
              <Trans>Meet sitters who’ll treat your dog like family</Trans>
            </ServiceTargetAudience.Display>
            <ServiceTargetAudience.Content>
              <Trans>
                Connect with a 5-star sitter who'll welcome your dog into their home when you're out
                of town—whether it’s{' '}
                <Link
                  href={serviceLinks[ServiceLandingEnum.OVERNIGHT_DOG_BOARDING]}
                  target="_blank"
                  rel="noreferrer"
                >
                  just for the weekend
                </Link>{' '}
                or{' '}
                <Link
                  href={serviceLinks[ServiceLandingEnum.LONG_TERM_DOG_BOARDING]}
                  target="_blank"
                  rel="noreferrer"
                >
                  over a few weeks
                </Link>
                . Dog boarding is great for:
              </Trans>
            </ServiceTargetAudience.Content>
            <ServiceTargetAudience.List>
              <ServiceTargetAudience.ListItem>
                <Trans>
                  Dogs of all ages and abilities, including{' '}
                  <Link
                    href={serviceLinks[ServiceLandingEnum.PUPPY_BOARDING]}
                    target="_blank"
                    rel="noreferrer"
                  >
                    puppies
                  </Link>
                </Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>
                  Pet parents looking for{' '}
                  <Link
                    href={serviceLinks[ServiceLandingEnum.DOG_KENNEL_ALTERNATIVE]}
                    target="_blank"
                    rel="noreferrer"
                  >
                    a safe, loving alternative to a kennel
                  </Link>
                </Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>Dogs who'd love to socialize with their sitter's pets</Trans>
              </ServiceTargetAudience.ListItem>
            </ServiceTargetAudience.List>
          </ServiceTargetAudience>

          <DogBoardingServiceFaqs />

          <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
            <ServiceLandingServices.Display>
              <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
            </ServiceLandingServices.Display>
          </ServiceLandingServices>

          <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
            <CitiesAndNeighborhoods.Display>
              <Trans>Thousands of dog boarders in cities and neighborhoods, including:</Trans>
            </CitiesAndNeighborhoods.Display>
          </CitiesAndNeighborhoods>

          <Box my="8x" data-qa-id="service-landing-download-app-callout">
            <AppStoreCallout />
          </Box>
        </Flex>
      </Section>
    </>
  );
};

export default DogBoarding;
